import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';

import { CipoDialogHeaderComponent } from '@shared/dialogs';
import { CipoDialogModule } from '@shared/modules/cipo-dialog.module';
import {
  A11yColorFilter,
  A11yContrast,
  A11yFontScale,
  A11yLineHeight,
  A11yReadingAid,
  A11ySaturation,
  A11yTextSpacing,
  initialUserState,
  UserStore,
} from '@signal-store';

@Component({
  selector: 'cipo-accessibility-preferences',
  templateUrl: './accessibility-preferences.component.html',
  imports: [CipoDialogModule, CipoDialogHeaderComponent, MatSlideToggleModule, MatDividerModule, MatButtonToggleModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessibilityPreferencesComponent {
  readonly userStore = inject(UserStore);

  readonly hasCustomSettings = computed(() => {
    const current = this.userStore.a11y();
    const defaults = initialUserState.a11y;
    const keys = Object.keys(defaults) as (keyof typeof defaults)[];
    return keys.some(key => current[key] !== defaults[key]);
  });

  setContrast(contrast: A11yContrast) {
    this.userStore.updateA11y({ contrast });
  }

  setReduceMotion(change: MatSlideToggleChange) {
    this.userStore.updateA11y({ reduceMotion: change.checked });
  }

  setFontScale(scale: A11yFontScale) {
    this.userStore.updateA11y({ fontScale: scale });
  }

  setTextSpacing(textSpacing: A11yTextSpacing) {
    this.userStore.updateA11y({ textSpacing });
  }

  setLineHeight(lineHeight: A11yLineHeight) {
    this.userStore.updateA11y({ lineHeight });
  }

  setSaturation(saturation: A11ySaturation) {
    this.userStore.updateA11y({ saturation });
  }

  setUnderlineLinks(change: MatSlideToggleChange) {
    this.userStore.updateA11y({ underlineLinks: change.checked });
  }

  setDyslexiaFont(change: MatSlideToggleChange) {
    this.userStore.updateA11y({ dyslexiaFont: change.checked });
  }

  setColorFilter(colorFilter: A11yColorFilter) {
    this.userStore.updateA11y({ colorFilter });
  }

  setReadingAid(readingAid: A11yReadingAid) {
    this.userStore.updateA11y({ readingAid });
  }

  setLargeCursor(change: MatSlideToggleChange) {
    this.userStore.updateA11y({ largeCursor: change.checked });
  }

  resetToDefaults() {
    this.userStore.updateA11y({ ...initialUserState.a11y });
  }
}
